import { ENV } from "@/config/env";
import { TOKEN_STORE_NAME } from "@/config/variant";
import { clearToken } from "@shared-ui/utils/auth";
import { resolveLocalViteSignInUrl } from "@shared-ui/utils/localVite";
import { clearReturnTo, returnToKey } from "@shared-ui/utils/returnTo";
import { clearLastTenant, lastTenantKey } from "@shared-ui/utils/lastTenant";
import { clearNavMemory } from "@/lib/nav/backTracker";
import { clearRememberedDates } from "@/lib/mypage/diaryDateMemory";
import { clearJustRecorded } from "@/lib/mypage/justRecorded";

/**
 * Portal へのログアウトリダイレクト。
 *
 * PRODUCT_DEV-748: もとは `utils/auth.ts` にあったが、同ファイルが
 * `checkRoleIsAdmin` を `AdminLayout` から再export していたため、ログアウトだけが
 * 欲しい呼び出し側まで管理者画面レイアウトをバンドルに引き込んでいた。
 * Minizo は社外へ配るデモで管理者画面のコードを dist に入れたくないため、
 * 副作用の無いこのモジュールへ分離した（権限判定は `utils/role.ts`）。
 */
export const handleLogout = (): void => {
  // Portal は別オリジンのため各プロダクトの localStorage を消せない。
  // リダイレクト前に自身のトークンを削除する（残すと再起動時に認証済み扱いになる）。
  clearToken(TOKEN_STORE_NAME);
  // PRODUCT_DEV-916: ログイン後の戻り先も消す。残ると、同じタブで次にログインした人が
  // 前の人の開こうとしていた画面へ飛ばされる。
  clearReturnTo(returnToKey(TOKEN_STORE_NAME));
  // PRODUCT_DEV-1129: 直前のテナントも消す。残ると、次にログインした人へ前の人のテナントを勧める。
  clearLastTenant(lastTenantKey(TOKEN_STORE_NAME));
  // PRODUCT_DEV-1043: 来た画面の記録（商談名・顧客名・検索語が入る）と見ていた日の記憶も消す。
  // 同じタブで次にログインした人の ← に、前の人の商談名が出ないように
  clearNavMemory();
  clearRememberedDates();
  clearJustRecorded();
  // PRODUCT_DEV-814: ローカル開発ノード(/vite/<product>/)では productId を付けない。
  // 付けると Portal が候補を 1 件に絞って自動リダイレクトし、戻り先が DDB Products の
  // productUrl（= dev 環境）になるため、ローカルでログアウトすると dev 環境へ着地する。
  // デプロイ済み環境では常に null なので挙動は変わらない。
  const isLocal =
    resolveLocalViteSignInUrl(
      window.location.pathname,
      window.location.origin,
    ) !== null;
  window.location.href = isLocal
    ? `${ENV.AUTH_PORTAL_URL}/?logout`
    : `${ENV.AUTH_PORTAL_URL}/?logout&productId=${ENV.PRODUCT_ID}`;
};
